import { ApiClient } from '_core/api/client';
import { makeFetchMany } from '_core/api/makeFetchMany';
import { ListResponse } from '_core/api/types';
import { DATE_FORMAT_API_DATE } from '_core/dates/formats';
import { parseDate } from '_core/dates/utils';
import { ISorting, sortingToApiQueryParam } from '_core/sorting';
import { snakeCase } from 'change-case';
import dayjs from 'dayjs';
import { DocumentStatus } from 'documents/types';
import { Payer, StartRepairDate, StopRepairDate } from 'rentProtocols/types';
import { VatRate } from 'vatRates/vatRates';

export const BASE_ENDPOINT = '/rent_protocols_sell';

interface IRentProtocolSellSerialized {
  author: number | null;
  authorModified: number | null;
  contract: number;
  created: string;
  date: string;
  dayRate: string;
  dayRateTotal: string;
  endDate: string;
  expensiveComponentsPayer: Payer;
  files: string[];
  fromRoutineRepairTariffPayer: Payer;
  fromScheduledRepairTariffPayer: Payer;
  hasRentRateRoutineRepair: boolean;
  hasRentRateScheduledRepair: boolean;
  id: number;
  modified: string;
  note: string;
  number: string;
  partnerSign: number;
  protocolName: string;
  protocolNumber: string;
  receiveTariffPayer: Payer;
  returnTariffPayer: Payer;
  routineRepairPayer: Payer;
  scheduledRepairPayer: Payer;
  signerAuthority: string;
  startDate: string;
  startRentRateRoutineRepair: StartRepairDate | '';
  startRentRateScheduledRepair: StartRepairDate | '';
  status: DocumentStatus;
  stopRentRateRoutineRepair: StopRepairDate | '';
  stopRentRateScheduledRepair: StopRepairDate | '';
  toRoutineRepairTariffPayer: Payer;
  toScheduledRepairTariffPayer: Payer;
  vatRate: VatRate;
  wagonType: number | null;
}

export interface IRentProtocolSell
  extends Omit<
    IRentProtocolSellSerialized,
    | 'created'
    | 'date'
    | 'endDate'
    | 'modified'
    | 'startDate'
    | 'startRentRateRoutineRepair'
    | 'startRentRateScheduledRepair'
    | 'stopRentRateRoutineRepair'
    | 'stopRentRateScheduledRepair'
  > {
  created: Date;
  date: Date;
  endDate: Date;
  modified: Date;
  startDate: Date;
  startRentRateRoutineRepair: StartRepairDate | null;
  startRentRateScheduledRepair: StartRepairDate | null;
  stopRentRateRoutineRepair: StopRepairDate | null;
  stopRentRateScheduledRepair: StopRepairDate | null;
}

function deserializeRentProtocolSell({
  created,
  date,
  endDate,
  modified,
  startDate,
  startRentRateRoutineRepair,
  startRentRateScheduledRepair,
  stopRentRateRoutineRepair,
  stopRentRateScheduledRepair,
  ...otherProps
}: IRentProtocolSellSerialized): IRentProtocolSell {
  return {
    ...otherProps,
    created: parseDate(created),
    date: parseDate(date),
    endDate: parseDate(endDate),
    modified: parseDate(modified),
    startDate: parseDate(startDate),
    startRentRateRoutineRepair: startRentRateRoutineRepair || null,
    startRentRateScheduledRepair: startRentRateScheduledRepair || null,
    stopRentRateRoutineRepair: stopRentRateRoutineRepair || null,
    stopRentRateScheduledRepair: stopRentRateScheduledRepair || null,
  };
}

export enum RentProtocolsSellSortingField {
  Contract = 'contract',
  Date = 'date',
  EndDate = 'endDate',
  OrderingNumber = 'orderingNumber',
  StartDate = 'startDate',
}

interface IFetchRentProtocolsSellParams {
  contract?: number;
  ids?: number[];
  page?: number;
  partner?: number;
  sorting?: Array<ISorting<RentProtocolsSellSortingField>>;
}

export async function fetchRentProtocolsSell(
  api: ApiClient,
  { contract, ids, page, partner, sorting }: IFetchRentProtocolsSellParams
): Promise<ListResponse<IRentProtocolSell>> {
  const response = await api.get<ListResponse<IRentProtocolSellSerialized>>(
    BASE_ENDPOINT,
    {
      contract,
      ids: ids?.join(','),
      ordering: sorting && sortingToApiQueryParam(sorting, snakeCase),
      page,
      partner,
    }
  );

  return {
    ...response,
    results: response.results.map(deserializeRentProtocolSell),
  };
}

export const fetchManyRentProtocolsSell = makeFetchMany(
  fetchRentProtocolsSell,
  'ids'
);

interface ICreateOrUpdateRentProtocolSellParams {
  contract: number;
  date: Date | null;
  dayRate: string;
  dayRateTotal: string;
  endDate: Date | null;
  expensiveComponentsPayer: Payer;
  files: string[];
  fromRoutineRepairTariffPayer: Payer;
  fromScheduledRepairTariffPayer: Payer;
  hasRentRateRoutineRepair: boolean;
  hasRentRateScheduledRepair: boolean;
  note: string;
  number: string;
  partnerSign: number | null;
  receiveTariffPayer: Payer;
  returnTariffPayer: Payer;
  routineRepairPayer: Payer;
  scheduledRepairPayer: Payer;
  signerAuthority: string;
  startDate: Date | null;
  startRentRateRoutineRepair: StartRepairDate | null;
  startRentRateScheduledRepair: StartRepairDate | null;
  status: DocumentStatus;
  stopRentRateRoutineRepair: StopRepairDate | null;
  stopRentRateScheduledRepair: StopRepairDate | null;
  toRoutineRepairTariffPayer: Payer;
  toScheduledRepairTariffPayer: Payer;
  vatRate: VatRate;
  wagonType: number | null;
}

function serializeCreateOrUpdateRentProtocolSellParams({
  date,
  endDate,
  startDate,
  startRentRateRoutineRepair,
  startRentRateScheduledRepair,
  stopRentRateRoutineRepair,
  stopRentRateScheduledRepair,
  ...otherParams
}: ICreateOrUpdateRentProtocolSellParams) {
  return {
    ...otherParams,
    date: date && dayjs(date).format(DATE_FORMAT_API_DATE),
    endDate: endDate && dayjs(endDate).format(DATE_FORMAT_API_DATE),
    startDate: startDate && dayjs(startDate).format(DATE_FORMAT_API_DATE),
    startRentRateRoutineRepair: startRentRateRoutineRepair || '',
    startRentRateScheduledRepair: startRentRateScheduledRepair || '',
    stopRentRateRoutineRepair: stopRentRateRoutineRepair || '',
    stopRentRateScheduledRepair: stopRentRateScheduledRepair || '',
  };
}

export async function createRentProtocolSell(
  api: ApiClient,
  params: ICreateOrUpdateRentProtocolSellParams
) {
  const createdProtocol = await api.post<IRentProtocolSellSerialized>(
    BASE_ENDPOINT,
    serializeCreateOrUpdateRentProtocolSellParams(params)
  );

  return deserializeRentProtocolSell(createdProtocol);
}

export async function updateRentProtocolSell(
  api: ApiClient,
  id: number,
  params: ICreateOrUpdateRentProtocolSellParams
) {
  const updatedProtocol = await api.put<IRentProtocolSellSerialized>(
    `${BASE_ENDPOINT}/${id}`,
    serializeCreateOrUpdateRentProtocolSellParams(params)
  );

  return deserializeRentProtocolSell(updatedProtocol);
}

export function getRentProtocolSellDocxDownloadLink(
  api: ApiClient,
  id: number
) {
  return api.buildApiUrl(`${BASE_ENDPOINT}/${id}/download_docx`);
}
